/**
 * Boundary Geometry Utilities
 *
 * Provides convex hull computation, buffer expansion, centroid calculation,
 * and vertex count analysis for field boundary polygons.
 *
 * Reference: /agent-os/specs/2026-02-27-field-boundary-editor/spec.md
 */

interface Coordinate {
  lat: number;
  lng: number;
}

/**
 * Compute the convex hull of a set of coordinates using Andrew's monotone chain algorithm.
 * Returns the vertices of the convex hull in counter-clockwise order.
 *
 * Handles edge cases: collinear points, duplicate points, fewer than 3 points.
 */
export function computeConvexHull(coordinates: Coordinate[]): Coordinate[] {
  if (coordinates.length < 3) {
    return [...coordinates];
  }

  // Remove duplicate points
  const unique = removeDuplicates(coordinates);
  if (unique.length < 3) {
    return [...unique];
  }

  // Sort by lng (x), then by lat (y) for ties
  const sorted = [...unique].sort((a, b) => a.lng - b.lng || a.lat - b.lat);

  // Build lower hull
  const lower: Coordinate[] = [];
  for (const point of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], point) <= 0) {
      lower.pop();
    }
    lower.push(point);
  }

  // Build upper hull
  const upper: Coordinate[] = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const point = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], point) <= 0) {
      upper.pop();
    }
    upper.push(point);
  }

  // Remove the last point of each half because it is repeated at the start of the other half
  lower.pop();
  upper.pop();

  return [...lower, ...upper];
}

/**
 * Expand a polygon outward by a buffer distance using simple centroid-outward scaling.
 * This is an approximation suitable for small buffer distances (~5-10 meters).
 * Not a full geodesic buffer -- sufficient for MVP spray width compensation.
 */
export function applyBuffer(coordinates: Coordinate[], metersBuffer: number): Coordinate[] {
  if (coordinates.length < 3 || metersBuffer <= 0) {
    return [...coordinates];
  }

  const centroid = computeCentroid(coordinates);

  // Convert buffer distance from meters to approximate degrees
  // 1 degree latitude is approximately 111,000 meters
  // 1 degree longitude varies by latitude: 111,000 * cos(latitude)
  const latToMeters = 111000;
  const lngToMeters = 111000 * Math.cos(centroid.lat * Math.PI / 180);

  return coordinates.map(coord => {
    const dLat = coord.lat - centroid.lat;
    const dLng = coord.lng - centroid.lng;

    // Distance from centroid in meters
    const distLat = dLat * latToMeters;
    const distLng = dLng * lngToMeters;
    const distance = Math.sqrt(distLat * distLat + distLng * distLng);

    if (distance === 0) {
      return { ...coord };
    }

    // Scale factor to expand outward by the buffer distance
    const scale = (distance + metersBuffer) / distance;

    return {
      lat: centroid.lat + dLat * scale,
      lng: centroid.lng + dLng * scale
    };
  });
}

/**
 * Compute the centroid (geometric center) from boundary coordinates.
 * Uses simple average of all coordinate values.
 */
export function computeCentroid(coordinates: Coordinate[]): Coordinate {
  if (coordinates.length === 0) {
    return { lat: 0, lng: 0 };
  }

  const sumLat = coordinates.reduce((sum, c) => sum + c.lat, 0);
  const sumLng = coordinates.reduce((sum, c) => sum + c.lng, 0);

  return {
    lat: sumLat / coordinates.length,
    lng: sumLng / coordinates.length
  };
}

/**
 * Extract vertex count from a GeoJSON polygon.
 * Handles Polygon and MultiPolygon geometry types.
 */
export function getVertexCount(geojson: any): number {
  if (!geojson) {
    return 0;
  }

  if (geojson.type === 'Polygon' && Array.isArray(geojson.coordinates)) {
    // Sum vertices across all rings (exterior + holes)
    return geojson.coordinates.reduce((count: number, ring: any[]) => count + (ring?.length ?? 0), 0);
  }

  if (geojson.type === 'MultiPolygon' && Array.isArray(geojson.coordinates)) {
    return geojson.coordinates.reduce((count: number, polygon: any[][]) => {
      return count + polygon.reduce((ringCount: number, ring: any[]) => ringCount + (ring?.length ?? 0), 0);
    }, 0);
  }

  // For Feature or FeatureCollection, recurse into geometry
  if (geojson.type === 'Feature' && geojson.geometry) {
    return getVertexCount(geojson.geometry);
  }

  return 0;
}

/**
 * Check if a GeoJSON polygon has a high vertex count.
 * Returns true if the vertex count meets or exceeds the threshold (default: 1000).
 */
export function isHighVertexCount(geojson: any, threshold: number = 1000): boolean {
  return getVertexCount(geojson) >= threshold;
}

/**
 * Calculate approximate area in acres from coordinates using the Shoelace formula.
 * Mirrors the existing logic from FlightMapService for consistency.
 */
export function calculateAreaAcres(coordinates: Coordinate[]): number {
  if (coordinates.length < 3) {
    return 0;
  }

  // Convert to meters using rough approximation
  const avgLat = coordinates.reduce((sum, c) => sum + c.lat, 0) / coordinates.length;
  const latToMeters = 111000;
  const lngToMeters = 111000 * Math.cos(avgLat * Math.PI / 180);

  // Calculate area using Shoelace formula
  let area = 0;
  for (let i = 0; i < coordinates.length; i++) {
    const j = (i + 1) % coordinates.length;
    const xi = coordinates[i].lng * lngToMeters;
    const yi = coordinates[i].lat * latToMeters;
    const xj = coordinates[j].lng * lngToMeters;
    const yj = coordinates[j].lat * latToMeters;

    area += xi * yj - xj * yi;
  }

  area = Math.abs(area) / 2;

  // Convert square meters to acres (1 acre = 4046.86 m^2)
  return area / 4046.86;
}

// -- Internal helpers --

/**
 * Cross product of vectors OA and OB where O is the origin point.
 * Positive = counter-clockwise, negative = clockwise, zero = collinear.
 */
function cross(o: Coordinate, a: Coordinate, b: Coordinate): number {
  return (a.lng - o.lng) * (b.lat - o.lat) - (a.lat - o.lat) * (b.lng - o.lng);
}

/**
 * Remove duplicate coordinates from an array.
 */
function removeDuplicates(coordinates: Coordinate[]): Coordinate[] {
  const seen = new Set<string>();
  return coordinates.filter(c => {
    const key = `${c.lat},${c.lng}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}
